/* Offene Zugangsanfragen (pending und approved — alles ohne Einladung).
 *
 * Der Zustand liegt im Modulbereich (Singleton): Der Hinweis in der
 * Kopfleiste und die Liste in der Nutzerverwaltung zeigen damit zwangslaeufig
 * dieselbe Zahl. Zwei getrennte Ladevorgaenge wuerden frueher oder spaeter
 * auseinanderlaufen — der Punkt am Konto-Knopf stuende dann noch, obwohl die
 * Anfrage laengst entschieden ist.
 */

import { ref, computed } from 'vue';
import {
  loadOpenRequests,
  decideRequest,
  inviteRequest,
  subscribeRequests,
  unsubscribeRequests,
} from '../supabase/requests.js';

const pending = ref([]);
const loading = ref(false);
const error = ref(null);
let channel = null;
let started = false;

export function useAccessRequests() {
  const pendingCount = computed(() => pending.value.length);

  async function refresh() {
    loading.value = true;
    error.value = null;
    try {
      pending.value = await loadOpenRequests();
    } catch (e) {
      error.value = e.message || 'loadFailed';
      pending.value = [];
    } finally {
      loading.value = false;
    }
  }

  /** Einmal pro Sitzung: laden und auf Aenderungen horchen. */
  async function start() {
    if (started) return;
    started = true;
    await refresh();
    /* Bei jeder Aenderung neu laden statt den Datensatz aus dem Ereignis zu
       uebernehmen: Eine Anfrage kann auch von einem anderen Admin entschieden
       worden sein, dann faellt sie aus der Liste heraus. */
    channel = subscribeRequests(() => refresh());
  }

  function stop() {
    unsubscribeRequests(channel);
    channel = null;
    started = false;
    pending.value = [];
  }

  function drop(id) {
    pending.value = pending.value.filter((r) => r.id !== id);
  }

  /** Anfrage ablehnen; sie verschwindet danach aus der Liste. */
  async function decide(id, decision) {
    await decideRequest(id, decision);
    drop(id);
  }

  /* Bei diesen Fehlern ist die Anfrage serverseitig erledigt (Status invited bzw. nicht
     mehr einladbar) — sie gehört nicht mehr in die Liste, auch wenn die Meldung ein Fehler ist. */
  const SETTLED = ['alreadyRegistered', 'roleNotSet', 'notInvitable'];

  /** Einladen: Mail verschicken und Rolle setzen; die Zeile verschwindet bei Erfolg. */
  async function invite(id, role) {
    try {
      await inviteRequest(id, role);
    } catch (e) {
      if (SETTLED.includes(e.message)) drop(id);
      throw e;
    }
    drop(id);
  }

  return { pending, pendingCount, loading, error, refresh, start, stop, decide, invite };
}
